import type { Task } from "@/types"

export type UrgencyLevel = 0 | 1 | 2 | 3

export interface TimeRemaining {
  text: string
  urgencyLevel: "critical" | "warning" | "safe"
}

/**
 * Get urgency level for a task based on days until expiry.
 *
 * Levels:
 * - 0: Normal (no urgency)
 * - 1: Approaching (warning - 2 days left)
 * - 2: Imminent (critical - 1 day or less)
 * - 3: Expired (dead)
 */
export function getUrgencyLevel(
  task: Pick<Task, "status" | "expired" | "days_until_expiry">,
  autoArchiveDays = 7,
): UrgencyLevel {
  if (task.status !== "active") return 0
  if (task.expired) return 3
  if (task.days_until_expiry === null) return 0

  // No urgency if auto archive is 1 day
  if (autoArchiveDays === 1) return 0

  // For auto archive < 3 days, only warn at 1 day
  if (autoArchiveDays < 3) {
    if (task.days_until_expiry <= 1) return 2
    return 0
  }

  // For auto archive >= 3 days
  if (task.days_until_expiry <= 1) return 2
  if (task.days_until_expiry === 2) return 1

  return 0
}

/**
 * Get the day name for a date string.
 */
function getDayName(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00")
  return date.toLocaleDateString("en-US", { weekday: "long" })
}

/**
 * Calculate days between two date strings (YYYY-MM-DD).
 * Returns positive if targetDate is in the future.
 */
function daysBetween(fromDate: string, toDate: string): number {
  const from = new Date(fromDate + "T00:00:00")
  const to = new Date(toDate + "T00:00:00")
  const diffMs = to.getTime() - from.getTime()
  return Math.round(diffMs / (1000 * 60 * 60 * 24))
}

/**
 * Calculate time remaining in a human-readable format with urgency level.
 * Uses calendar-day semantics with simple, non-dramatic language.
 * Let the urgency colors do the emotional work instead of the text.
 *
 * @param deathDate - The date (YYYY-MM-DD) the task dies (last day alive)
 * @param today - Today's date (YYYY-MM-DD) in user's timezone
 */
export function getTimeRemaining(
  deathDate: string | null,
  today: string | null,
): TimeRemaining {
  if (!deathDate || !today) {
    return { text: "", urgencyLevel: "safe" }
  }

  const daysUntilDeath = daysBetween(today, deathDate)

  if (daysUntilDeath < 0) {
    return { text: "Expired", urgencyLevel: "critical" }
  }

  if (daysUntilDeath === 0) {
    return { text: "Today", urgencyLevel: "critical" }
  }

  if (daysUntilDeath === 1) {
    return { text: "Tomorrow", urgencyLevel: "critical" }
  }

  if (daysUntilDeath <= 6) {
    const dayName = getDayName(deathDate)
    const urgencyLevel = daysUntilDeath <= 2 ? "warning" : "safe"
    return { text: dayName, urgencyLevel }
  }

  if (daysUntilDeath <= 11) {
    return { text: `${daysUntilDeath} days`, urgencyLevel: "safe" }
  }

  if (daysUntilDeath <= 16) {
    return { text: "~2 weeks", urgencyLevel: "safe" }
  }

  if (daysUntilDeath <= 23) {
    return { text: "~3 weeks", urgencyLevel: "safe" }
  }

  return { text: "~1 month", urgencyLevel: "safe" }
}

/**
 * Legacy getTimeRemaining for backwards compatibility.
 * Uses expires_at timestamp directly (less accurate for calendar display).
 * @deprecated Use getTimeRemaining(deathDate, today) instead
 */
export function getTimeRemainingLegacy(expiresAt: string): TimeRemaining {
  const now = new Date()
  const expires = new Date(expiresAt)
  const diffMs = expires.getTime() - now.getTime()

  if (diffMs <= 0) {
    return { text: "Expired", urgencyLevel: "critical" }
  }

  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)

  if (hours < 24) {
    return { text: "Today", urgencyLevel: "critical" }
  }

  if (hours < 48) {
    return { text: "Tomorrow", urgencyLevel: "critical" }
  }

  if (days <= 3) {
    return { text: `${days} days`, urgencyLevel: "warning" }
  }

  return { text: `${days} days`, urgencyLevel: "safe" }
}

/**
 * Get accessibility label for task expiry status.
 */
export function getExpiryAriaLabel(
  task: Pick<Task, "status" | "expired" | "death_date">,
  today: string | null,
): string {
  if (task.status !== "active") return ""
  if (task.expired) return "This task has died"
  if (!task.death_date || !today) return ""

  const daysUntilDeath = daysBetween(today, task.death_date)

  if (daysUntilDeath < 0) return "This task has died"
  if (daysUntilDeath === 0) return "This task dies today"
  if (daysUntilDeath === 1) return "This task dies tomorrow"
  if (daysUntilDeath === 2) return "This task dies in 2 days"
  return ""
}
